/**
 * RESPONSIBILITY: the annotations that go on a CLASS.
 *
 *   @Section       declare a section — this is what registers it
 *   @EditableBy    who may write it. Omitted ⇒ NONE. Never list admin.
 *   @ViewableBy    who may read it.  Omitted ⇒ ALL, including anonymous.
 *   @Row           an item type for @List — annotated like a section, never registered
 */
import type { Audience } from '../index';
import type { SectionOptions } from './types';
import { finaliseSection, stageEditable, stageViewable } from './registry';

export function Section(opts: SectionOptions): ClassDecorator {
  return (ctor) => finaliseSection(ctor, opts.name, opts.label, opts.always === true);
}

/**
 * Who may WRITE this section. Omit and it defaults to [NONE] — read-only.
 * Never list Admin: admin bypasses every check by definition.
 */
export function EditableBy(roles: Audience): ClassDecorator {
  return (ctor) => stageEditable(ctor, roles);
}

/**
 * Who may READ this section. Omit and it defaults to [ALL] — including anonymous.
 * Scope it and everyone else gets a PermissionNotGranted object, not the content.
 */
export function ViewableBy(roles: Audience): ClassDecorator {
  return (ctor) => stageViewable(ctor, roles);
}

/**
 * Row type for @List.
 *
 * Deliberately does nothing: its fields stay in the registry's `pending` map, where
 * rowFields() reads them lazily. A row is a shape, not a section — it has no name, no
 * permissions and no storage of its own.
 */
export function Row(): ClassDecorator {
  return () => { /* fields stay pending, read lazily by the schema generators */ };
}
